import { apiJson } from "./apiClient";

export interface AuditorSummary {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  current_audit_count: number;
  past_audit_count: number;
}

export interface EligibleAuditor {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  current_audit_count: number;
}

export interface AuditorAssignment {
  certification_id: number;
  auditor_id: string;
}

export interface AuditorAssignmentCertification {
  certification_id: number;
  lab_id: string;
  lab_name: string;
  lab_type: "wet" | "dry" | string;
  champion_email: string | null;
  certification_level: "bronze" | "silver" | "gold" | string;
  certification_state: string;
  started_at: string;
  audit_ready_at: string | null;
  auditor_id: string | null;
  auditor_name: string | null;
  auditor_email: string | null;
  auditor_checklist_submitted: boolean;
}

export interface AssessmentStatus {
  assessment_type: string;
  baseline_state: string | null;
  savings_state: string | null;
}

export interface AuditorDashboardItem {
  certification_id: number;
  lab_id: string;
  lab_name: string;
  lab_type: "wet" | "dry" | string;
  champion_email: string | null;
  certification_level: "bronze" | "silver" | "gold" | string;
  certification_state: string;
  started_at: string;
  assessments: AssessmentStatus[];
  auditor_checklist_submitted: boolean;
}

export interface AuditorChecklistQuestion {
  id: number;
  question: string;
  lab_type: string;
  level: string;
  for_auditor: boolean;
  suggestion: string | null;
}

export interface AuditorChecklistAnswer {
  checklist_id: number;
  answer: "y" | "n" | "x" | null;
  na_reason: number | null;
  na_reason_label: string | null;
  na_comment: string | null;
  updated_by: string | null;
  updated_at: string;
}

export interface AuditorCertificationDetail extends AuditorDashboardItem {
  lab_checklist_questions: AuditorChecklistQuestion[];
  lab_checklist_answers: AuditorChecklistAnswer[];
  lab_checklist_submitted: boolean;
  lab_checklist_comments: string | null;
  lab_checklist_revision: number | null;
  lab_checklist_instance_id: number | null;
  lab_checklist_submission_id: number | null;
  auditor_checklist_questions: AuditorChecklistQuestion[];
  auditor_checklist_answers: AuditorChecklistAnswer[];
  auditor_checklist_revision: number | null;
  auditor_checklist_instance_id: number | null;
  auditor_checklist_submission_id: number | null;
}

export interface AuditorActionResult {
  certification_id: number;
  state: string;
}

export class AuditorService {
  static async listAuditors(): Promise<AuditorSummary[]> {
    return apiJson<AuditorSummary[]>("/auditors");
  }

  static async listAssignableCertifications(): Promise<
    AuditorAssignmentCertification[]
  > {
    return apiJson<AuditorAssignmentCertification[]>(
      "/auditors/certifications/assignable"
    );
  }

  static async listEligibleAuditors(
    certificationId: number
  ): Promise<EligibleAuditor[]> {
    return apiJson<EligibleAuditor[]>(
      `/auditors/certifications/${certificationId}/eligible`
    );
  }

  static async assignAuditor(
    certificationId: number,
    auditorId: string
  ): Promise<AuditorAssignment> {
    return apiJson<AuditorAssignment>(
      `/auditors/certifications/${certificationId}/assign`,
      {
        method: "POST",
        body: JSON.stringify({ auditor_id: auditorId }),
      }
    );
  }

  static async removeAuditor(
    certificationId: number
  ): Promise<AuditorAssignment> {
    return apiJson<AuditorAssignment>(
      `/auditors/certifications/${certificationId}/assign`,
      {
        method: "DELETE",
      }
    );
  }

  static async clearPendingInvite(
    certificationId: number
  ): Promise<{ certification_id: number }> {
    return apiJson<{ certification_id: number }>(
      `/auditors/certifications/${certificationId}/invite`,
      {
        method: "DELETE",
      }
    );
  }

  static async getDashboard(): Promise<AuditorDashboardItem[]> {
    return apiJson<AuditorDashboardItem[]>("/auditors/dashboard");
  }

  static async getCertificationDetail(
    certificationId: number
  ): Promise<AuditorCertificationDetail> {
    return apiJson<AuditorCertificationDetail>(
      `/auditors/certifications/${certificationId}`
    );
  }

  static async certify(
    certificationId: number,
    expiresAt?: string
  ): Promise<AuditorActionResult> {
    return apiJson<AuditorActionResult>(
      `/auditors/certifications/${certificationId}/certify`,
      {
        method: "POST",
        body: JSON.stringify(expiresAt ? { expires_at: expiresAt } : {}),
      }
    );
  }

  static async requestReview(
    certificationId: number,
    comments: string
  ): Promise<AuditorActionResult> {
    return apiJson<AuditorActionResult>(
      `/auditors/certifications/${certificationId}/request-review`,
      {
        method: "POST",
        body: JSON.stringify({ comments }),
      }
    );
  }
}
